import { Quiz } from '../quiz-api';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';
import {applyMerge} from "../../../graphql/conflictHandlers/applyMerge";
import {Notification} from "../../../API";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerIdAndWsId', 'creator', 'createdAt', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 */
const SYSTEM_FIELDS = ['version', 'updatedAt'];

/**
 * Conflict handler for Quiz entities.
 * Handles version conflicts during quiz updates with intelligent merging.
 * 
 * Merge Strategy:
 * 1. Start with complete currentState (all server fields)
 * 2. Apply user's intendedChanges except for immutable/system fields
 * 3. Always use currentState.version for retry
 */
export const quizConflictHandler: ConflictHandler<Quiz> = {
    entityType: 'Quiz',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<Quiz> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'Quiz refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<Quiz>): Partial<Quiz> {
        const merged: Partial<Quiz> & Record<string, any> = {};

        // Start with current state
        Object.assign(merged, currentState);

        applyMerge<Quiz>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        (merged as any).version = (currentState as any).version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
